/**
 * Approximate yard size from Google Geocoder geometry + fallbacks (no parcel files).
 *
 * 1) Prefer viewport/bounds area via spherical.computeArea (needs "geometry" library).
 * 2) If that fails OR the box is huge (neighborhood/city), fall back to a **typical-lot
 *    circle** around the geocoded point, scaled by Google's `location_type`.
 */

const SQ_METERS_TO_SQ_FT = 10.76391041671;

/** Above this, the geocoder box is almost never a single yard — use circle fallback. */
const VIEWPORT_MAX_SQ_FT = 250000; // ~5.7 acres

/** Above this, even viewport estimate is discarded as non-lot (sanity). */
const VIEWPORT_HARD_CAP_SQ_FT = 5_000_000; // ~115 acres

/**
 * Default search radius (meters) around the pin when viewport is unusable or too big.
 * Tuned so πr² ≈ common suburban lot orders of magnitude.
 */
const RADIUS_M_BY_LOCATION_TYPE = {
  ROOFTOP: 20, // ~0.31 acre
  RANGE_INTERPOLATED: 28, // ~0.6 acre
  GEOMETRIC_CENTER: 40,
  APPROXIMATE: 55,
};

function getLocationType(geometry) {
  const t = geometry?.location_type;
  if (typeof t === "string") return t;
  return "UNKNOWN";
}

function latLngFromGeocoderLocation(loc) {
  if (!loc) return null;
  const lat = typeof loc.lat === "function" ? loc.lat() : loc.lat;
  const lng = typeof loc.lng === "function" ? loc.lng() : loc.lng;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return new window.google.maps.LatLng(lat, lng);
}

/**
 * Normalize bounds-like object to NE/SW corners (supports LatLngBounds or literals).
 */
function getBoundsCorners(rect) {
  if (!rect) return null;

  if (typeof rect.getNorthEast === "function" && typeof rect.getSouthWest === "function") {
    return { ne: rect.getNorthEast(), sw: rect.getSouthWest() };
  }

  const n = rect.north ?? rect.northeast?.lat ?? rect.north_east?.lat;
  const s = rect.south ?? rect.southwest?.lat ?? rect.south_west?.lat;
  const e = rect.east ?? rect.northeast?.lng ?? rect.north_east?.lng;
  const w = rect.west ?? rect.southwest?.lng ?? rect.south_west?.lng;

  if ([n, s, e, w].every((x) => Number.isFinite(x))) {
    return {
      ne: new window.google.maps.LatLng(n, e),
      sw: new window.google.maps.LatLng(s, w),
    };
  }

  return null;
}

function rectangleAreaSqMeters(ne, sw) {
  const spherical = window.google?.maps?.geometry?.spherical;
  if (!spherical || !ne || !sw) return null;

  const nw = new window.google.maps.LatLng(ne.lat(), sw.lng());
  const se = new window.google.maps.LatLng(sw.lat(), ne.lng());
  const path = [nw, ne, se, sw];
  const sqMeters = spherical.computeArea(path);
  return Number.isFinite(sqMeters) && sqMeters > 0 ? sqMeters : null;
}

function circleAreaSqMeters(radiusM) {
  return Math.PI * radiusM * radiusM;
}

/**
 * @param {google.maps.GeocoderResult} result
 * @returns {{ sqft: number, locationType: string, method: string, note?: string } | null}
 */
export function estimateLotAreaFromGeocoderResult(result) {
  const g = window.google?.maps;
  if (!result?.geometry || !g) return null;

  const { geometry } = result;
  const locationType = getLocationType(geometry);
  const center = latLngFromGeocoderLocation(geometry.location);
  if (!center) return null;

  const radiusM =
    RADIUS_M_BY_LOCATION_TYPE[locationType] ?? RADIUS_M_BY_LOCATION_TYPE.APPROXIMATE;

  // Try viewport / bounds rectangle first
  const rect = geometry.bounds || geometry.viewport;
  const corners = getBoundsCorners(rect);
  let sqMeters = corners ? rectangleAreaSqMeters(corners.ne, corners.sw) : null;

  let note;

  if (sqMeters != null) {
    const sqft = Math.round(sqMeters * SQ_METERS_TO_SQ_FT);
    if (sqft > VIEWPORT_HARD_CAP_SQ_FT) {
      sqMeters = null;
    } else if (sqft > VIEWPORT_MAX_SQ_FT) {
      sqMeters = null;
      note =
        "Map search box was larger than a typical yard; using a default lot radius around your pin instead.";
    } else {
      return {
        sqft,
        locationType,
        method: geometry.bounds ? "bounds" : "viewport",
      };
    }
  }

  // Fallback: circle around geocoded point
  sqMeters = circleAreaSqMeters(radiusM);
  const sqft = Math.round(sqMeters * SQ_METERS_TO_SQ_FT);
  return {
    sqft,
    locationType,
    method: "typical_lot_radius",
    note:
      note ||
      "Estimated using a typical yard-sized radius around the map pin (geocoder does not provide parcel outlines).",
  };
}
